import type { NodeViewProps } from '@tiptap/react';
import { NodeViewWrapper } from '@tiptap/react';
import type { ResizeCallback } from 're-resizable';
import { Resizable } from 're-resizable';
import type { CSSProperties } from 'react';
import type { InlineImageAttrs } from './inlineImageAttributes';
import { getImageAppearanceStyle, getImageSizeStyle } from './inlineImageAttributes';

export const RESIZE_HANDLES_CLASS = 'inline-image-resize-handles';
const RESIZE_HANDLE_SIZE = 10;

const HANDLE_OFFSET = -RESIZE_HANDLE_SIZE / 2;

const resizeHandleStyle: CSSProperties = {
  width: RESIZE_HANDLE_SIZE,
  height: RESIZE_HANDLE_SIZE,
  background: '#ffffff',
  border: '1px solid #1976d2',
  borderRadius: 2,
  boxSizing: 'border-box',
};

// re-resizable's defaults make edge handles span the whole edge and push corner handles further out;
// centre every handle on the edge it resizes instead.
const resizeHandleStyles: Record<'right' | 'bottom' | 'bottomRight', CSSProperties> = {
  right: { ...resizeHandleStyle, top: '50%', right: HANDLE_OFFSET, transform: 'translateY(-50%)' },
  bottom: { ...resizeHandleStyle, left: '50%', bottom: HANDLE_OFFSET, transform: 'translateX(-50%)' },
  bottomRight: { ...resizeHandleStyle, right: HANDLE_OFFSET, bottom: HANDLE_OFFSET },
};

const resizeHandles = { right: true, bottom: true, bottomRight: true };

export const InlineImageNodeView = ({ node, editor, selected, getPos, updateAttributes }: NodeViewProps) => {
  const attrs = node.attrs as InlineImageAttrs;
  const { width, height } = attrs;

  // With `true`, re-resizable reads the ratio from the rendered image, which covers images without a stored size.
  const lockAspectRatio = attrs.lockAspectRatio && (width && height ? width / height : true);

  // A click on an image in a block that is not being edited belongs to the block (select, then enter edit mode).
  // Double-click covers the second click landing before the editor becomes editable.
  const selectImage = () => {
    if (editor.isEditable) editor.chain().focus().setNodeSelection(getPos()).run();
  };

  const onResizeStop: ResizeCallback = (_event, _direction, element) => {
    updateAttributes({ width: Math.round(element.offsetWidth), height: Math.round(element.offsetHeight) });
  };

  return (
    <NodeViewWrapper as="span" onClick={selectImage} onDoubleClick={selectImage}>
      <Resizable
        as="span"
        size={{ width: width ?? 'auto', height: 'auto' }}
        style={{ display: 'inline-block', verticalAlign: 'bottom', aspectRatio: getImageSizeStyle(attrs).aspectRatio }}
        maxWidth="100%"
        minWidth={RESIZE_HANDLE_SIZE * 2}
        minHeight={RESIZE_HANDLE_SIZE * 2}
        lockAspectRatio={lockAspectRatio}
        enable={editor.isEditable && selected ? resizeHandles : false}
        handleStyles={resizeHandleStyles}
        handleWrapperClass={RESIZE_HANDLES_CLASS}
        onResizeStop={onResizeStop}
      >
        <img
          src={attrs.src ?? undefined}
          alt={attrs.alt ?? ''}
          title={attrs.title ?? undefined}
          draggable={false}
          style={{ ...getImageAppearanceStyle(attrs), display: 'block', width: '100%', height: '100%' }}
        />
      </Resizable>
    </NodeViewWrapper>
  );
};
